import { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import InfoPopover, { PopLead } from 'features/actions/workspace/InfoPopover'

const formatWhen = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const formatDay = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' })
}

const VISIBLE = 5

const ImportedTaskDetails = ({ task }) => {
  const [showAll, setShowAll] = useState(false)
  const imported = task?.metadata?.imported
  if (!imported) return null

  const checks = imported.checks || []
  const shown = showAll ? checks : checks.slice(0, VISIBLE)
  const historyOnly = Boolean(imported.historyOnly)
  const related = historyOnly ? imported.related : null
  const bpId = task?.building?.id || task?.buildingPassportId

  if (imported.kind === 'user_task' || imported.kind === 'asset_inspection') {
    const inspection = imported.kind === 'asset_inspection'
    const done = Boolean(imported.completedAt)
    const asset = imported.asset || {}
    const missingPeople = done ? [] : imported.peoplePending || imported.notOnBuildingPassport || []
    const comments = imported.comments || []
    const notes = imported.notes || []
    const missingFiles = Number(imported.filesNotRetrieved || 0)
    const rows = [
      ...(inspection
        ? [
            ['Asset', asset.name],
            ['Asset type', asset.type],
            ['Identifier', asset.identifier],
            ['Additional information', (asset.information || []).join(', ')],
          ]
        : [
            ['Set up by', imported.owner],
            ['Repeats in the previous system', imported.schedule],
            ['Priority in the previous system', imported.priorityLabel],
          ]),
      ['Assigned in the previous system', (imported.assignedTo || []).join(', ')],
      ...(done ? [['Done', [formatWhen(imported.completedAt), imported.completedBy && `by ${imported.completedBy}`].filter(Boolean).join(' ')]] : []),
    ].filter(([, value]) => value)
    const lead = inspection
      ? 'This inspection was recorded in the previous system and brought across exactly as recorded.'
      : done
        ? 'This task was done in the previous system and brought across with its comments and files.'
        : imported.schedule
          ? 'This task was set up in the previous system and brought across. It repeats here on the same schedule.'
          : 'This task was set up in the previous system and brought across with its comments and files.'
    return (
      <Wrap>
        <Head>
          <Title>From the previous system</Title>
          <InfoPopover label='What does this mean?' title='Brought across from the previous system'>
            <PopLead>{inspection ? 'This asset inspection' : 'This task'} was recorded in the system used before Building Passport. Its details, comments and files were brought across exactly as they were.</PopLead>
            {!done && imported.schedule && (
              <PopLead>It repeats here on the same schedule. Each new occurrence appears on its due date and is given to the same people.</PopLead>
            )}
            <PopLead>It is not linked to a compliance obligation, so it does not change the compliance score.</PopLead>
          </InfoPopover>
        </Head>
        <Lead>{lead}</Lead>
        {rows.length > 0 && (
          <List>
            {rows.map(([label, value]) => (
              <Item key={label}>
                <CheckMeta>{label}</CheckMeta>
                <CheckTitle>{value}</CheckTitle>
              </Item>
            ))}
          </List>
        )}
        {Number(imported.datesBroughtAcross || 0) > 0 && (
          <Lead>
            The previous system set the next {imported.datesBroughtAcross} dates in a way Building Passport cannot repeat, so each of those dates was brought across as it was.
            {imported.continuesFrom ? ` After the last one, it carries on here from ${formatDay(imported.continuesFrom)}.` : ''}
          </Lead>
        )}
        {missingPeople.length > 0 && (
          <Notice role='note'>
            {missingPeople.join(', ')} {missingPeople.length === 1 ? 'is' : 'are'} not on this Building Passport yet. Once {missingPeople.length === 1 ? 'they are' : 'each of them is'} given access to the building, this task is assigned to them automatically and they start getting reminders.
          </Notice>
        )}
        {notes.length > 0 && (
          <>
            <SubTitle>On the task form</SubTitle>
            <List>
              {notes.map(note => (
                <Item key={note.label}>
                  <CheckMeta>{note.label}</CheckMeta>
                  <Body>{note.value}</Body>
                </Item>
              ))}
            </List>
          </>
        )}
        {comments.length > 0 && (
          <>
            <SubTitle>Comment history</SubTitle>
            <List>
              {comments.map((comment, index) => (
                <Item key={`${comment.at || ''}-${index}`}>
                  <CheckMeta>
                    {comment.author && <span>{comment.author}</span>}
                    {formatWhen(comment.at) && <span>{formatWhen(comment.at)}</span>}
                  </CheckMeta>
                  <Body>{comment.body}</Body>
                </Item>
              ))}
            </List>
          </>
        )}
        {missingFiles > 0 && (
          <Lead>{missingFiles === 1 ? '1 file attached in the previous system could not be retrieved.' : `${missingFiles} files attached in the previous system could not be retrieved.`}</Lead>
        )}
      </Wrap>
    )
  }

  if (imported.kind === 'audit') {
    const missing = Number(imported.photosNotRetrieved || 0)
    const rows = [
      ['Audit', imported.title],
      ['Carried out by', (imported.assessors || []).join(', ')],
      ['Started', formatDay(imported.startedAt)],
      ['Reviewed', formatWhen(imported.reviewedAt)],
      ['Published', formatWhen(imported.publishedAt)],
    ].filter(([, value]) => value)
    return (
      <Wrap>
        <Head>
          <Title>From the previous system</Title>
          <InfoPopover label='What does this mean?' title='Brought across from the previous system'>
            <PopLead>This audit was carried out and published in the system used before Building Passport.</PopLead>
            <PopLead>Every answer, comment and photo was brought across onto this form exactly as recorded. Actions raised from the audit were brought across separately as remedials on the building.</PopLead>
            <PopLead>Because the audit was completed, it counts towards the compliance score for the annual audit obligation.</PopLead>
          </InfoPopover>
        </Head>
        <Lead>Carried out and published in the previous system, then brought across with every answer, comment and photo.</Lead>
        <List>
          {rows.map(([label, value]) => (
            <Item key={label}>
              <CheckMeta>{label}</CheckMeta>
              <CheckTitle>{value}</CheckTitle>
            </Item>
          ))}
        </List>
        {missing > 0 && (
          <Lead>{missing === 1 ? '1 photo from this audit could not be retrieved from the previous system.' : `${missing} photos from this audit could not be retrieved from the previous system.`}</Lead>
        )}
      </Wrap>
    )
  }

  return (
    <Wrap>
      <Head>
        <Title>From the previous system</Title>
        <InfoPopover label='What does this mean?' title='Brought across from the previous system'>
          {historyOnly ? (
            <>
              <PopLead>This check was carried out and recorded in the system used before Building Passport.</PopLead>
              {related ? (
                <PopLead>It belongs to the “{related.checklist}” checklist, so it is linked there for reference. It was recorded on its own rather than as part of the full checklist, so it does not count towards the compliance score.</PopLead>
              ) : (
                <PopLead>It is not one of the checks in the building’s current compliance programme, so it is kept as a record of the work. It does not count towards the compliance score.</PopLead>
              )}
            </>
          ) : (
            <>
              <PopLead>These checks were carried out and recorded in the system used before Building Passport, then brought across onto this checklist.</PopLead>
              <PopLead>The previous system only recorded that a check was done, not a Yes or No answer, so each check that was done is shown as Yes. Any comments written at the time are in the comments under each question.</PopLead>
              <PopLead>Because the checklist was completed, it counts towards the compliance score for its obligation, like any other completed task.</PopLead>
            </>
          )}
        </InfoPopover>
      </Head>
      <Lead>
        {historyOnly
          ? related
            ? 'Recorded in the previous system as part of the checklist below. Shown for reference; it does not count towards the compliance score.'
            : 'Recorded in the previous system. Kept as a record; it does not count towards the compliance score.'
          : `${checks.length === 1 ? '1 check was' : `${checks.length} checks were`} recorded as done in the previous system and brought across onto this checklist.`}
      </Lead>
      {related && (
        <>
          <SubTitle>Part of</SubTitle>
          <List>
            <Item>
              <CheckTitle>{related.checklist}</CheckTitle>
              {related.question && <Body>{related.question}</Body>}
            </Item>
          </List>
          {bpId && related.obligationId && (
            <TrackerLink to={`/bp-list/${bpId}/programme?obligation=${related.obligationId}`}>
              View this checklist on the compliance tracker
            </TrackerLink>
          )}
        </>
      )}
      {Number(imported.siteBuildings || 0) > 1 && (
        <Lead>
          The previous system recorded these checks once for the whole site, which is {imported.siteBuildings} buildings here, so they are recorded on each of them.
          {imported.evidenceOn ? ` Any evidence files are on ${imported.evidenceOn}.` : ''}
        </Lead>
      )}
      {checks.length > 0 && (
        <List>
          {shown.map(check => (
            <Item key={check.externalId || check.title}>
              <CheckTitle>{check.title}</CheckTitle>
              <CheckMeta>
                {formatWhen(check.completedAt) && <span>Done {formatWhen(check.completedAt)}</span>}
                {check.completedBy && <span>by {check.completedBy}</span>}
                {!check.completedBy && check.assignedTo?.length > 0 && <span>assigned to {check.assignedTo.join(', ')}</span>}
                {check.fromExport && <span>from the final export</span>}
              </CheckMeta>
            </Item>
          ))}
        </List>
      )}
      {checks.length > VISIBLE && (
        <ToggleButton type='button' onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Show fewer' : `Show all ${checks.length} checks`}
        </ToggleButton>
      )}
    </Wrap>
  )
}

export default ImportedTaskDetails

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Lead = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti};
`

const List = styled.ul`
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: white;
  border: 1px solid #edf2f7;
`

const CheckTitle = styled.span`
  font-size: 15px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};
`

const CheckMeta = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const SubTitle = styled.h3`
  margin: 4px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Body = styled.span`
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.haiti};
`

const Notice = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #fde68a;
  background: #fef3c7;
  font-size: 15px;
  line-height: 1.5;
  color: #78350f;
`

const ToggleButton = styled.button`
  align-self: flex-start;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #cfeef2;
  background: white;
  color: #0e6f7a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #f0fbfc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const TrackerLink = styled(Link)`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #cfeef2;
  background: white;
  color: #0e6f7a;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: #f0fbfc;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`
